import { HEADER_NAME_MPT } from '@lmn/nextjs-appfw/dist/common';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { CasperTokenClaims } from '@/app/common/casperTokenClaims.ts';
import { readCasperTokenClaims } from '@/app/common/casperTokenClaims.ts';
import { TSX_REQUEST_HEADER_NAME } from '@/app/pageTypes.ts';
import { API_CALL_PREFIX } from '@/app/components/search/utils/api/apiUtils.ts';
import { API_NAME } from '@/app/components/search/common/apiTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';

// MPH-1410: the tab's own Casper token. The page renders with a token and a CSRF token signed for it (commonData),
// and before this module the tab kept them for its whole life, so after about 45 minutes, or once another tab or page
// moved the __mpt cookie on, its API calls lost the signed-in account id. This module renews the pair in place:
// shortly before expiry, on return to the tab and on the proxy's x-tsx-mpt-renew signal, and shares it with the other
// TSX tabs. The two fetch helpers (clientAPICall and fetchWithTracing) read the pair from here at send time.
//
// Deliberately outside React: a renewal re-renders nothing, and no SWR fetcher or long-lived callback can hold a
// stale pair. Nothing is written to browser storage. Inert on the server, where a module-level store would be shared
// across requests, and until started, when the helpers get exactly commonData's pair.

export type TabCredentials = { mpt: string; csrfToken: string };

export type RenewalTrigger = 'preflight' | 'timer' | 'visible' | 'signal';

type CasperTokenMessage = { type: 'casper-token'; mpt: string; csrfToken: string };

export const CASPER_TOKEN_CHANNEL_NAME = 'tsx-casper-token';
const MESSAGE_TYPE = 'casper-token';

// A call within this many seconds of the token's expiry waits for a renewal, for at most PREFLIGHT_MAX_WAIT_MS.
const PREFLIGHT_WINDOW_SECS = 60;
const PREFLIGHT_MAX_WAIT_MS = 1500;
// The timer renews this long before expiry, give or take the jitter, so the preflight rarely has to wait.
const RENEW_BEFORE_EXPIRY_SECS = 5 * 60;
const TIMER_JITTER_MS = 60 * 1000;
// Back in the tab after this long away, a signed-in token is renewed: another tab or page may have moved the cookie.
const HIDDEN_RENEW_AFTER_MS = 5 * 60 * 1000;
const RETURN_RENEW_MIN_INTERVAL_MS = 2 * 60 * 1000;
// Never more than one answered renewal a minute, whatever the triggers or a skewed clock say.
const MIN_RENEW_INTERVAL_MS = 60 * 1000;
// Casper's tokens live about 45 minutes. A pair from another tab that claims to live longer than this, by the server's
// clock, is not one Casper gave: adopting it would make the tab refuse every real renewal answer until a reload.
const MAX_ADOPTED_LIFETIME_SECS = 50 * 60;
// A renewal still unanswered after this long is abandoned and counts as a failure, so a stalled request (a dead
// connection after a wake or a network switch) cannot keep every trigger and call waiting on it. Above the route's
// own Casper budget of 3 seconds plus one retry.
const RENEWAL_TIMEOUT_MS = 10 * 1000;
// A renewal the server answered with an error backs off for long: retrying soon would only load Casper again.
const BACKOFF_START_MS = 60 * 1000;
const BACKOFF_MAX_MS = 15 * 60 * 1000;
// A renewal that got no answer (the request failed or stalled) never reached the server, or not usefully, so it
// retries soon, whatever navigator.onLine says: it may be wrong, and an online event may never come.
const UNANSWERED_BACKOFF_START_MS = 2 * 1000;
const UNANSWERED_BACKOFF_MAX_MS = 60 * 1000;
// setTimeout fires at once for a delay above 2^31 - 1 ms.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

type Store = {
  generation: number;
  started: boolean;
  pair: TabCredentials | undefined;
  claims: CasperTokenClaims | undefined;
  getSearchParams: () => SearchParams;
  // The server's clock minus the local one, from commonData.serverTime and every renewal answer.
  clockOffsetMs: number;
  inFlight: Promise<boolean> | undefined;
  lastRenewalAt: number;
  lastReturnRenewalAt: number;
  // The backoff after a renewal the server answered with an error.
  backoffMs: number;
  // The backoff after a renewal that got no answer; any answered renewal resets it.
  unansweredBackoffMs: number;
  backoffUntil: number;
  // The last renewal got no answer (offline, a dropped connection or a stall), so coming back online may retry at once.
  lastFailureUnanswered: boolean;
  hiddenAt: number | undefined;
  timer: ReturnType<typeof setTimeout> | undefined;
  channel: BroadcastChannel | undefined;
  removeListeners: (() => void) | undefined;
};

const createStore = (generation: number): Store => ({
  generation,
  started: false,
  pair: undefined,
  claims: undefined,
  getSearchParams: () => ({}),
  clockOffsetMs: 0,
  inFlight: undefined,
  lastRenewalAt: 0,
  lastReturnRenewalAt: 0,
  backoffMs: 0,
  unansweredBackoffMs: 0,
  backoffUntil: 0,
  lastFailureUnanswered: false,
  hiddenAt: undefined,
  timer: undefined,
  channel: undefined,
  removeListeners: undefined,
});

let store: Store = createStore(0);

// Decoded once per token: the helpers ask on every call.
let lastDecoded: { mpt: string; claims: CasperTokenClaims | undefined } = { mpt: '', claims: undefined };
const claimsOf = (mpt: string | undefined): CasperTokenClaims | undefined => {
  if (!mpt) return undefined;
  if (lastDecoded.mpt !== mpt) lastDecoded = { mpt, claims: readCasperTokenClaims(mpt) };
  return lastDecoded.claims;
};

const serverNowMs = (): number => Date.now() + store.clockOffsetMs;

const updateClockOffset = (serverTime: unknown): void => {
  const serverMs = typeof serverTime === 'string' ? Date.parse(serverTime) : NaN;
  if (Number.isFinite(serverMs)) store.clockOffsetMs = serverMs - Date.now();
};

const commonDataPair = (commonData: CommonData): TabCredentials => ({
  mpt: commonData.mpt,
  csrfToken: commonData.csrfToken,
});

// A pair counts as newer only with a later expiry: Casper stamps every answer with a later one, and all TSX tabs share
// one cookie jar and one CSRF nonce, so the newest answer is valid in every tab, whoever it names.
const isLater = (candidate: CasperTokenClaims | undefined, reference: CasperTokenClaims | undefined): boolean =>
  !!candidate && (!reference || candidate.exp > reference.exp);

// Elapsed local time since a moment; a clock moved backwards counts as long enough ago rather than blocking forever.
const elapsedSince = (moment: number): number => {
  const elapsed = Date.now() - moment;
  return elapsed < 0 ? Number.POSITIVE_INFINITY : elapsed;
};

const needsAccountIdRenewal = (claims: CasperTokenClaims | undefined): boolean => !!claims?.hasAccountId;

const isNearExpiry = (claims: CasperTokenClaims | undefined): boolean =>
  !!claims && serverNowMs() / 1000 >= claims.exp - PREFLIGHT_WINDOW_SECS;

const postPair = (): void => {
  if (!store.channel || !store.pair?.mpt) return;
  const message: CasperTokenMessage = { type: MESSAGE_TYPE, mpt: store.pair.mpt, csrfToken: store.pair.csrfToken };
  try {
    store.channel.postMessage(message);
  } catch {
    // A closed channel: the other tabs catch up on their own triggers.
  }
};

// The server answered a renewal while the tab is visible: whatever absence came before it has been dealt with.
const endAbsence = (): void => {
  if (document.visibilityState !== 'hidden') store.hiddenAt = undefined;
};

const canRenewNow = (): boolean =>
  !!store.inFlight || (Date.now() >= store.backoffUntil && elapsedSince(store.lastRenewalAt) >= MIN_RENEW_INTERVAL_MS);

function schedule(): void {
  if (store.timer) clearTimeout(store.timer);
  store.timer = undefined;
  // Anonymous tokens are never used upstream (the server keeps the cookie for them), so they are never renewed on time.
  if (!store.started || !needsAccountIdRenewal(store.claims)) return;
  const jitter = (Math.random() * 2 - 1) * TIMER_JITTER_MS;
  const untilRenewal = ((store.claims?.exp ?? 0) - RENEW_BEFORE_EXPIRY_SECS) * 1000 + jitter - serverNowMs();
  const untilAllowed = Math.max(
    store.backoffUntil - Date.now(),
    store.lastRenewalAt ? MIN_RENEW_INTERVAL_MS - elapsedSince(store.lastRenewalAt) : 0
  );
  const delay = Math.min(Math.max(untilRenewal, untilAllowed, 0), MAX_TIMEOUT_MS);
  const { generation } = store;
  store.timer = setTimeout(() => {
    if (store.generation !== generation) return;
    store.timer = undefined;
    renew('timer').then(() => {
      // A renewal that ran has rescheduled; one that was not allowed to run must not leave the tab without a timer.
      if (store.generation === generation && !store.timer) schedule();
    });
  }, delay);
}

function adopt(pair: TabCredentials, claims: CasperTokenClaims): void {
  store.pair = pair;
  store.claims = claims;
  schedule();
}

// onUnanswered undoes what the trigger stamped when the request got no answer at all, so it still counts as not done.
async function sendRenewal(trigger: RenewalTrigger, onUnanswered: () => void): Promise<boolean> {
  const { generation } = store;
  const sent = store.pair;
  const controller = typeof AbortController === 'undefined' ? undefined : new AbortController();
  let timeout: ReturnType<typeof setTimeout> | undefined;
  // Set once the server answered with an HTTP status, whatever it was.
  let answered = false;
  // Rejects at the deadline even when the request ignores the abort, so the catch branch always runs.
  const deadline = new Promise<never>((_, reject) => {
    timeout = setTimeout(() => {
      // A body still unread at the deadline is as good as no answer: the stalled-renewal abandon counts as one.
      answered = false;
      controller?.abort();
      reject(new Error('renewal timed out'));
    }, RENEWAL_TIMEOUT_MS);
  });
  try {
    const response = await Promise.race([
      fetch(`${API_CALL_PREFIX}${API_NAME.CASPER_TOKEN}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          ...(sent?.mpt ? { [HEADER_NAME_MPT]: sent.mpt } : {}),
          ...(sent?.csrfToken ? { [TSX_REQUEST_HEADER_NAME.CSRF_TOKEN]: sent.csrfToken } : {}),
        },
        body: JSON.stringify({ searchParams: store.getSearchParams(), trigger }),
        signal: controller?.signal,
      }),
      deadline,
    ]);
    // fetch rejects without a status when the request got no answer at all: offline, a dropped connection, the abort.
    answered = true;
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const answer = await Promise.race([response.json(), deadline]);
    const claims = readCasperTokenClaims(typeof answer?.mpt === 'string' ? answer.mpt : '');
    if (!claims || typeof answer.csrfToken !== 'string' || !answer.csrfToken) throw new Error('unusable answer');
    if (store.generation !== generation) return false;
    updateClockOffset(answer.serverTime);
    store.backoffMs = 0;
    store.unansweredBackoffMs = 0;
    store.backoffUntil = 0;
    store.lastFailureUnanswered = false;
    endAbsence();
    // A newer pair adopted from another tab while this one was in flight wins.
    if (!store.claims || claims.exp >= store.claims.exp) {
      adopt({ mpt: answer.mpt, csrfToken: answer.csrfToken }, claims);
      postPair();
    } else {
      schedule();
    }
    return true;
  } catch {
    if (store.generation !== generation) return false;
    // The old pair stays; the calls go on with it and the server copes as it does today.
    store.lastFailureUnanswered = !answered;
    if (answered) {
      store.backoffMs = store.backoffMs ? Math.min(store.backoffMs * 2, BACKOFF_MAX_MS) : BACKOFF_START_MS;
      store.unansweredBackoffMs = 0;
      store.backoffUntil = Date.now() + store.backoffMs;
      // An answer, even an error, ends the absence the way a renewal does: from here the backoff decides.
      endAbsence();
    } else {
      store.unansweredBackoffMs = store.unansweredBackoffMs
        ? Math.min(store.unansweredBackoffMs * 2, UNANSWERED_BACKOFF_MAX_MS)
        : UNANSWERED_BACKOFF_START_MS;
      store.backoffUntil = Date.now() + store.unansweredBackoffMs;
      // It used up neither the one-a-minute limit nor the absence and the trigger limits: the next trigger after the
      // short backoff may renew again.
      onUnanswered();
    }
    schedule();
    return false;
  } finally {
    clearTimeout(timeout);
  }
}

// One renewal at a time: every trigger that fires while one is in flight shares it.
function renew(trigger: RenewalTrigger, onUnanswered?: () => void): Promise<boolean> {
  if (!store.started) return Promise.resolve(false);
  if (store.inFlight) return store.inFlight;
  if (!canRenewNow()) return Promise.resolve(false);
  const previousRenewalAt = store.lastRenewalAt;
  store.lastRenewalAt = Date.now();
  const inFlight = sendRenewal(trigger, () => {
    store.lastRenewalAt = previousRenewalAt;
    onUnanswered?.();
  }).finally(() => {
    if (store.inFlight === inFlight) store.inFlight = undefined;
  });
  store.inFlight = inFlight;
  return inFlight;
}

// Back in the tab (visible, focus, a bfcache restore, back online). A sleeping laptop or a hidden tab may have missed
// its timer, and another tab or page may have moved the cookie on, which no timer can see.
function onReturnToTab(): void {
  // A focus or online event can arrive while the tab is still hidden: the absence is not over yet, so its start is
  // kept for the visibilitychange that ends it, and only the expiry check runs.
  const stillHidden = document.visibilityState === 'hidden';
  const hiddenFor = stillHidden || store.hiddenAt === undefined ? 0 : elapsedSince(store.hiddenAt);
  const { claims } = store;
  const longAbsence = needsAccountIdRenewal(claims) && hiddenFor >= HIDDEN_RENEW_AFTER_MS;
  // A long absence ends only once the server answered a renewal (endAbsence): a renewal that got no answer or could
  // not start yet leaves it for the next return event after the backoff, such as the online event that follows a wake.
  if (!stillHidden && !longAbsence) store.hiddenAt = undefined;
  if (!needsAccountIdRenewal(claims)) return;
  const awayLongEnough = longAbsence && elapsedSince(store.lastReturnRenewalAt) >= RETURN_RENEW_MIN_INTERVAL_MS;
  if (isNearExpiry(claims) || awayLongEnough) {
    if (store.inFlight || !canRenewNow()) {
      renew('visible');
      return;
    }
    const previousReturnRenewalAt = store.lastReturnRenewalAt;
    store.lastReturnRenewalAt = Date.now();
    renew('visible', () => {
      store.lastReturnRenewalAt = previousReturnRenewalAt;
    });
  } else {
    schedule();
  }
}

function onMessage(event: MessageEvent): void {
  const data = event?.data as Partial<CasperTokenMessage> | undefined;
  if (data?.type !== MESSAGE_TYPE || typeof data.mpt !== 'string' || typeof data.csrfToken !== 'string') return;
  const claims = readCasperTokenClaims(data.mpt);
  if (!claims || claims.exp > serverNowMs() / 1000 + MAX_ADOPTED_LIFETIME_SECS) return;
  // Adopting costs no Casper call; it is not announced again, so two tabs cannot echo each other.
  if (isLater(claims, store.claims)) adopt({ mpt: data.mpt, csrfToken: data.csrfToken }, claims);
}

function openChannel(): void {
  if (store.channel || typeof BroadcastChannel === 'undefined') return;
  try {
    store.channel = new BroadcastChannel(CASPER_TOKEN_CHANNEL_NAME);
    store.channel.addEventListener('message', onMessage);
  } catch {
    // Without BroadcastChannel each tab renews on its own triggers.
    store.channel = undefined;
  }
}

function closeChannel(): void {
  try {
    store.channel?.close();
  } catch {
    // Already closed.
  }
  store.channel = undefined;
}

function addListeners(): () => void {
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') store.hiddenAt = store.hiddenAt ?? Date.now();
    else onReturnToTab();
  };
  // Chromium evicts a page from the back/forward cache when its open channel receives a message, and every TSX page
  // posts on start, so the channel is closed while the page is cached and reopened on restore. The return-to-tab rule
  // covers whatever was announced in between.
  const onPageHide = (event: PageTransitionEvent) => {
    store.hiddenAt = store.hiddenAt ?? Date.now();
    if (event.persisted) closeChannel();
  };
  const onPageShow = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    openChannel();
    onReturnToTab();
  };
  const onFocus = () => onReturnToTab();
  // A wake often fires focus or visibilitychange before the network is back, and that renewal fails at once. It got no
  // answer, so its short backoff is lifted when the device comes back online, and the return check can renew right away
  // instead of letting the calls go out with an expired token. The failed renewal left the one-a-minute limit, the
  // absence and the trigger limits as they were, so a long absence is still renewed here.
  const onOnline = () => {
    if (store.lastFailureUnanswered && !store.inFlight) {
      store.lastFailureUnanswered = false;
      store.backoffUntil = 0;
    }
    onReturnToTab();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('focus', onFocus);
  window.addEventListener('online', onOnline);
  return () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('online', onOnline);
  };
}

function stopTabCasperToken(): void {
  if (store.timer) clearTimeout(store.timer);
  store.removeListeners?.();
  closeChannel();
  store = createStore(store.generation + 1);
}

type StartTabCasperToken = {
  commonData: CommonData;
  getSearchParams: () => SearchParams;
};

// Started once by PageWrapperClient on every page. Returns the function that stops it.
export function startTabCasperToken({ commonData, getSearchParams }: StartTabCasperToken): () => void {
  if (typeof window === 'undefined') return () => undefined;
  stopTabCasperToken();
  store.started = true;
  store.getSearchParams = getSearchParams;
  // A tab opened in the background (a middle click on a hotel) is hidden from the start, and its absence counts from
  // now: no visibilitychange to hidden will come before the traveller first switches to it.
  if (document.visibilityState === 'hidden') store.hiddenAt = Date.now();
  updateClockOffset(commonData.serverTime);
  const claims = claimsOf(commonData.mpt);
  if (commonData.mpt && claims) {
    store.pair = commonDataPair(commonData);
    store.claims = claims;
  }
  store.removeListeners = addListeners();
  openChannel();
  // Announced on start, so the tabs this page's Casper answer superseded catch up.
  postPair();
  schedule();
  const { generation } = store;
  return () => {
    if (store.generation === generation) stopTabCasperToken();
  };
}

// A soft refresh brings a new commonData pair from a new Casper answer: adopted when it is newer, and announced.
export function offerTabCredentials(commonData: CommonData): void {
  if (!store.started) return;
  const claims = claimsOf(commonData.mpt);
  if (!commonData.mpt || !claims || !isLater(claims, store.claims)) return;
  updateClockOffset(commonData.serverTime);
  adopt(commonDataPair(commonData), claims);
  postPair();
}

// The pair to send now: the newer of the module's and commonData's, or exactly commonData's when not started.
export function getTabCredentials(commonData: CommonData): TabCredentials {
  if (!store.started || !store.pair) return commonDataPair(commonData);
  return isLater(claimsOf(commonData.mpt), store.claims) ? commonDataPair(commonData) : store.pair;
}

// The preflight, the guarantee whatever the timers did (a sleeping laptop's timers do not run): a token with ua.aid
// within a minute of expiry by the server's clock waits for one shared renewal, at most PREFLIGHT_MAX_WAIT_MS, then
// goes out with whatever pair is current. A call made while any renewal is in flight waits for it the same way,
// whatever the expiry: a return-to-tab or signal renewal is replacing a token the server may already refuse.
// Undefined when no wait is needed, so a caller can send synchronously.
export function getPreflightWait(commonData: CommonData): Promise<TabCredentials> | undefined {
  if (!store.started) return undefined;
  let renewal = store.inFlight;
  if (!renewal) {
    const claims = claimsOf(getTabCredentials(commonData).mpt);
    if (!needsAccountIdRenewal(claims) || !isNearExpiry(claims) || !canRenewNow()) return undefined;
    renewal = renew('preflight');
  }
  let cap: ReturnType<typeof setTimeout> | undefined;
  const capped = new Promise<void>((resolve) => {
    cap = setTimeout(resolve, PREFLIGHT_MAX_WAIT_MS);
  });
  return Promise.race([renewal.then(() => undefined), capped]).then(() => {
    clearTimeout(cap);
    return getTabCredentials(commonData);
  });
}

export async function getFreshTabCredentials(commonData: CommonData): Promise<TabCredentials> {
  return (await getPreflightWait(commonData)) ?? getTabCredentials(commonData);
}

// The proxy's x-tsx-mpt-renew: the server refused this tab's token while the cookie names a signed-in traveller
// without an account id. Renews anonymous tokens too, which covers a sign-in in another tab. Only a refusal of the
// token the tab still holds counts: an answer to a call sent with a pair the tab has since replaced (renewed, or
// adopted from another tab or a soft refresh) says nothing about the current one. A burst of signals renews once, and
// the one-a-minute limit bounds the rest, for a cookie that keeps moving or a Casper that never gives ua.aid.
export function reportRenewSignal(reason: string | null | undefined, sentMpt?: string): void {
  if (!store.started || !reason) return;
  if (sentMpt && isLater(store.claims, claimsOf(sentMpt))) return;
  if (store.inFlight || !canRenewNow()) return;
  renew('signal');
}

// For the helpers: reads the signal from any answer, error statuses included, and never throws. sentMpt is the token
// the call carried.
export function reportRenewSignalFromResponse(response: Response | undefined, sentMpt?: string): void {
  try {
    reportRenewSignal(response?.headers?.get(TSX_REQUEST_HEADER_NAME.MPT_RENEW), sentMpt);
  } catch {
    // An answer without readable headers carries no signal.
  }
}
